import { router } from '@inertiajs/react'
import SystemAdminLayout from '@/layouts/SystemAdminLayout'
import AdminPaginationBar from '@/components/AdminPaginationBar'
import type { AdminPagination } from '@/types'

interface Entry {
  id: number
  admin_email_address: string | null
  action: string
  subject_type: string | null
  subject_id: string | null
  subject_label: string | null
  metadata: Record<string, unknown> | null
  ip_address: string | null
  occurred_at: string
}

interface Props {
  entries: Entry[]
  pagination: AdminPagination
  action_options: string[]
  action_filter: string | null
}

export default function AuditLog({ entries, pagination, action_options, action_filter }: Props) {
  const filterBy = (value: string) => {
    router.get('/system_admin/audit-log', value ? { action_filter: value } : {}, { preserveState: true })
  }

  return (
    <SystemAdminLayout>
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Audit Log</h1>
          <p className="text-sm text-base-content/60">Every admin action, attributed and timestamped</p>
        </div>
        <select className="select select-sm w-56" value={action_filter ?? ''} onChange={(e) => filterBy(e.target.value)}>
          <option value="">All actions</option>
          {action_options.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>

      <div className="card bg-base-100">
        <div className="card-body p-0">
          <table className="table table-sm">
            <thead><tr><th>When</th><th>Admin</th><th>Action</th><th>Subject</th><th>Detail</th><th>IP</th></tr></thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id}>
                  <td className="text-sm whitespace-nowrap">{new Date(e.occurred_at).toLocaleString()}</td>
                  <td className="text-sm">{e.admin_email_address}</td>
                  <td><span className="badge badge-soft badge-sm font-mono">{e.action}</span></td>
                  <td className="text-sm">
                    {e.subject_type
                      ? <>{e.subject_label ?? `${e.subject_type} #${e.subject_id}`} <span className="text-xs text-base-content/40">({e.subject_type})</span></>
                      : <span className="text-base-content/40">—</span>}
                  </td>
                  <td className="font-mono text-xs text-base-content/60 max-w-60 truncate">
                    {e.metadata && Object.keys(e.metadata).length > 0 ? JSON.stringify(e.metadata) : '—'}
                  </td>
                  <td className="font-mono text-xs">{e.ip_address ?? '—'}</td>
                </tr>
              ))}
              {entries.length === 0 && (
                <tr><td colSpan={6} className="text-center text-base-content/40 py-8">No entries yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <AdminPaginationBar pagination={pagination} baseUrl="/system_admin/audit-log" params={{ action_filter: action_filter ?? undefined }} />
    </SystemAdminLayout>
  )
}
